import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import { reviews, Review } from '../data/reviews';
interface ReviewsProps {
  /** Show only the first N reviews (used for the homepage preview). */
  limit?: number;
}

/** Grid of real customer reviews from Google and Yelp. */
export function Reviews({
  limit
}: ReviewsProps) {
  const list = limit ? reviews.slice(0, limit) : reviews;
  return <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-315" data-mp="2w9d6npw2wemy">
      {list.map((review, index) => <ReviewCard key={review.name} review={review} index={index} data-id="element-316" data-mp="309x46ukkdk9m" data-mpi={index} />)}
    </ul>;
}
function ReviewCard({
  review,
  index
}: {
  review: Review;
  index: number;
}) {
  const isYelp = review.platform === 'Yelp';
  return <motion.li initial={{
    y: 30,
    opacity: 0
  }} whileInView={{
    y: 0,
    opacity: 1
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    delay: Math.min(index, 5) * 0.05,
    duration: 0.3
  }} className="flex flex-col h-full bg-zinc-900/40 border border-zinc-800 rounded-xl p-6" data-id="element-317" data-mp="3a3dvnrx8ana4">
      <div className="flex items-center justify-between mb-6" data-id="element-318" data-mp="2mdkjfruc6cds">
        {isYelp ? <img src="https://cdn.magicpatterns.com/uploads/175DFDXm2rpoLToMVtVWMh/yelp_burst.png" alt="Yelp review" className="h-6 w-auto" loading="lazy" decoding="async" data-id="element-319" data-mp="2mc9h7wgjhcos" /> : <svg className="h-6 w-auto" viewBox="0 0 24 24" fill="none" role="img" aria-label="Google review" data-id="element-320" data-mp="2ddxui5ismz1m">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-321" data-mp="3a1u24kw96k0p" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-322" data-mp="30d3rkkn5dnq3" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-323" data-mp="2w9nwvtun1xg1" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-324" data-mp="3o24tdp1h60me" />
          </svg>}
        <div className="flex gap-1" aria-label={`${review.stars} out of 5 stars`} data-id="element-325" data-mp="1r4qttnh29uj5">
          {Array.from({
          length: review.stars
        }).map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" aria-hidden="true" data-id="element-326" data-mp="1cdqhv8h3vii9" data-mpi={i} />)}
        </div>
      </div>

      <blockquote className="relative flex-1 mb-6" data-id="element-327" data-mp="13f68wz1ckn78">
        <Quote className="absolute -top-2 -left-2 w-8 h-8 text-zinc-800 rotate-180" aria-hidden="true" data-id="element-328" data-mp="13dla2x802i3k" />
        <p className="relative z-10 text-gray-300 leading-relaxed text-sm md:text-base italic pl-4" data-id="element-329" data-mp="18falwf31qzsp">
          "{review.text}"
        </p>
      </blockquote>

      <div className="mt-auto flex items-center gap-3 pt-4 border-t border-zinc-800/50" data-id="element-330" data-mp="yf0cz6o1oejc">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${isYelp ? 'bg-red-900/20 text-red-500' : 'bg-blue-900/20 text-blue-500'}`} aria-hidden="true" data-id="element-331" data-mp="2nu828a7fhuoj">
          {review.name.charAt(0)}
        </div>
        <div data-id="element-332" data-mp="h6r0pixx10at">
          <p className="font-bold text-white" data-id="element-333" data-mp="h4egt08lkfv4">{review.name}</p>
          <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-334" data-mp="c8pg2tkucsx8">
            {review.platform} Review
          </p>
        </div>
      </div>
    </motion.li>;
}